import { useEffect, useMemo, useState, useCallback } from "react";
import { X, MagnifyingGlass, Check, Clock, Warning, ArrowClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import { fmtTimeInTZ, offsetLabelForTZ } from "@/lib/timezones";

/**
 * FindATimeModal — visual cross-TZ availability scanner.
 *
 * Renders a participant × time-slot matrix. Cells are colored:
 *   green  = available (within working hours, no conflicts)
 *   amber  = off_hours (outside that participant's working window in their TZ)
 *   red    = conflict (already has another meeting overlapping this slot)
 *
 * Clicking a slot column header bubbles up to onPick({startUtc, endUtc}) so the
 * caller can prefill the meeting form. Only slots where ALL invited participants
 * are simultaneously available will be marked as "perfect" with a Check icon.
 */
export default function FindATimeModal({ open, onClose, onPick, participantIds, viewerTz, duration }) {
  const [date, setDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const scan = useCallback(async () => {
    if (!participantIds?.length || !date) return;
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/meetings/availability", {
        participant_ids: participantIds,
        date,
        viewer_tz: viewerTz || "UTC",
        duration_minutes: duration || 30,
        slot_minutes: 30,
        window_start: "06:00",
        window_end: "22:00",
      });
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [participantIds, date, viewerTz, duration]);

  useEffect(() => { if (open) scan(); }, [open, scan]);

  const userArr = useMemo(() => {
    if (!data) return [];
    return participantIds.map((uid) => data.users[uid]).filter(Boolean);
  }, [data, participantIds]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[var(--border)] w-full max-w-[1100px] max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="find-a-time-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-[var(--accent)]">/ AVAILABILITY SCANNER</div>
            <div className="font-display font-extrabold text-[20px] leading-tight">Find a time everyone can meet</div>
            <div className="text-[11px] text-[var(--text-muted)] font-mono mt-0.5">
              Times shown in <span className="font-semibold text-[var(--text-primary)]">{viewerTz}</span> ({offsetLabelForTZ(new Date(), viewerTz)}). Working hours respect each teammate&apos;s saved TZ.
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--bg-sidebar)]" data-testid="find-a-time-close"><X size={16} /></button>
        </div>

        {/* Controls */}
        <div className="px-5 py-3 border-b border-[var(--border)] bg-[var(--bg-sidebar)] flex flex-wrap items-end gap-3">
          <div>
            <label className="block font-mono text-[10px] tracking-widest text-[var(--text-muted)] mb-1">DATE</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input min-w-[170px]"
              data-testid="find-a-time-date"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] tracking-widest text-[var(--text-muted)] mb-1">DURATION</label>
            <div className="font-mono text-[13px] font-semibold px-3 py-2 border border-[var(--border)] bg-white">
              {duration} min
            </div>
          </div>
          <div className="flex-1" />
          <button onClick={scan} disabled={loading} className="btn-ghost flex items-center gap-1.5" data-testid="find-a-time-refresh">
            <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> {loading ? "Scanning…" : "Re-scan"}
          </button>
          <Legend />
        </div>

        {/* Matrix */}
        <div className="flex-1 overflow-auto p-5">
          {err && (
            <div className="border border-[var(--danger)] bg-red-50 text-[var(--danger)] text-[13px] p-3 flex items-start gap-2 mb-3" data-testid="find-a-time-error">
              <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
              <div>{err}</div>
            </div>
          )}
          {!data && !err && (
            <div className="flex items-center justify-center text-[12px] text-[var(--text-muted)] font-mono py-12" data-testid="find-a-time-loading">
              {loading ? "Scanning…" : "Pick a date to begin."}
            </div>
          )}
          {data && data.slots.length > 0 && (
            <div className="border border-[var(--border)] overflow-hidden" data-testid="find-a-time-grid">
              {/* Header row: time slot columns */}
              <div className="grid sticky top-0 z-10 bg-white border-b border-[var(--border)]"
                   style={{ gridTemplateColumns: `200px repeat(${data.slots.length}, minmax(28px, 1fr))` }}>
                <div className="px-3 py-2 font-mono text-[10px] tracking-widest text-[var(--text-muted)] border-r border-[var(--border)] sticky left-0 bg-white z-20">
                  TEAMMATE / SLOT
                </div>
                {data.slots.map((s, idx) => {
                  const label = fmtTimeInTZ(s.start_utc, viewerTz);
                  const showLabel = idx % 2 === 0; // every hour
                  return (
                    <div key={s.start_utc} className="border-r border-[var(--border)] text-center py-1 font-mono text-[10px] text-[var(--text-muted)]">
                      {showLabel ? label : ""}
                    </div>
                  );
                })}
              </div>

              {/* "Everyone" summary row */}
              <div className="grid border-b border-[var(--border)] bg-[var(--bg-sidebar)]"
                   style={{ gridTemplateColumns: `200px repeat(${data.slots.length}, minmax(28px, 1fr))` }}>
                <div className="px-3 py-2 text-[12px] font-bold flex items-center gap-1.5 sticky left-0 bg-[var(--bg-sidebar)] z-10 border-r border-[var(--border)]">
                  <Clock size={12} /> Everyone
                </div>
                {data.slots.map((s) => {
                  const cls = overallColor(s.overall);
                  return (
                    <button
                      key={s.start_utc}
                      onClick={() => onPick({ startUtc: s.start_utc, endUtc: s.end_utc })}
                      title={`${fmtTimeInTZ(s.start_utc, viewerTz)} — ${overallLabel(s.overall)}`}
                      className={`h-7 border-r border-[var(--border)] transition-opacity hover:opacity-70 ${cls} ${s.overall === "available" ? "ring-1 ring-emerald-600/40" : ""}`}
                      data-testid={`avail-slot-${s.start_utc}`}
                    >
                      {s.overall === "available" && <Check size={10} className="text-white mx-auto" weight="bold" />}
                    </button>
                  );
                })}
              </div>

              {/* Per-user rows */}
              {userArr.map((u) => (
                <div key={u.id} className="grid border-b border-[var(--border)]"
                     style={{ gridTemplateColumns: `200px repeat(${data.slots.length}, minmax(28px, 1fr))` }}>
                  <div className="px-3 py-2 flex items-center gap-2 sticky left-0 bg-white z-10 border-r border-[var(--border)]" data-testid={`avail-row-${u.id}`}>
                    {u.avatar_url && <img src={u.avatar_url} className="w-6 h-6 border border-[var(--border)]" alt="" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] font-semibold truncate">{u.name}</div>
                      <div className="text-[9px] font-mono text-[var(--text-muted)] truncate">
                        {u.timezone} {u.working_hours?.start}-{u.working_hours?.end}
                      </div>
                    </div>
                  </div>
                  {data.slots.map((s) => {
                    const cell = s.per_user[u.id];
                    const cls = cellColor(cell?.status);
                    return (
                      <div
                        key={s.start_utc}
                        title={cell?.title || statusLabel(cell?.status)}
                        className={`h-7 border-r border-[var(--border)] ${cls}`}
                        data-testid={`avail-cell-${u.id}-${s.start_utc}`}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          )}
          {data && data.slots.length === 0 && (
            <div className="text-[12px] text-[var(--text-muted)] py-12 text-center" data-testid="find-a-time-empty">
              No slots in the scan window. Try a different date.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-3 text-[10px] font-mono text-[var(--text-muted)]" data-testid="find-a-time-legend">
      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 inline-block" />AVAILABLE</span>
      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-300 inline-block" />OFF HOURS</span>
      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-rose-400 inline-block" />CONFLICT</span>
    </div>
  );
}

function overallColor(overall) {
  if (overall === "available") return "bg-emerald-500 cursor-pointer";
  if (overall === "partial_conflict") return "bg-rose-400/60 cursor-not-allowed";
  return "bg-amber-300/70 cursor-not-allowed";
}
function overallLabel(overall) {
  return overall === "available" ? "Everyone is free — click to pick" : overall === "partial_conflict" ? "At least one conflict" : "Outside working hours for someone";
}
function cellColor(status) {
  if (status === "available") return "bg-emerald-500";
  if (status === "conflict") return "bg-rose-400";
  if (status === "off_hours") return "bg-amber-300";
  return "bg-slate-200";
}
function statusLabel(status) {
  return status === "available" ? "Available" : status === "conflict" ? "Conflict" : status === "off_hours" ? "Off working hours" : "Unknown";
}
